// SF5 — Seasonal Stock Recommendations.
//
// AN ORDER QUANTITY, NOT A HOLDING LEVEL. The number in the order column is
// what to buy, already net of stock on hand and stock inbound. Making the seller
// subtract those themselves is the step where a restock decision goes wrong, so
// this surface never shows a level and never shows a negative.
//
// The two assumptions that drive every quantity sit above the table and are
// adjustable in place, because a plan built on someone else's lead time is not a
// plan. Where a figure is a default rather than the seller's own, it says so.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../Seasonal.module.css';
import { getStockPlan, saveInputs } from '../services/seasonalApi';
import SyncProgress from '../../components/SyncProgress';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const SERVICE_LEVELS = [0.80, 0.85, 0.90, 0.95, 0.98, 0.99];

const formatDate = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso
    : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const StockPlan = ({ asin }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Edited freely and applied on demand, so a half-typed lead time does not
  // refetch the whole plan on every keystroke.
  const [draft, setDraft] = useState(null);
  const [applied, setApplied] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getStockPlan(token, {
        asin,
        leadTimeDays: applied.leadTimeDays,
        serviceLevel: applied.serviceLevel,
      });
      setData(result);
      setDraft((current) => current || {
        leadTimeDays: result.inputs?.lead_time_days,
        serviceLevel: result.inputs?.service_level,
      });
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, asin, applied]);

  useEffect(() => { load(); }, [load]);

  const plan = data?.plan;
  const months = plan?.months || [];
  const inputs = data?.inputs;
  const assumed = inputs?.assumed || [];

  const persist = async () => {
    if (!token || !draft) return;
    setSaving(true);
    try {
      await saveInputs(token, {
        lead_time_days: draft.leadTimeDays,
        service_level: draft.serviceLevel,
      });
      setApplied({});
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>What to order</h2>
        {data?.stock?.available && (
          <span className={local.tierChip}>
            {inr.format(data.stock.on_hand || 0)} on hand
            {' · '}{inr.format(data.stock.in_transit || 0)} inbound
          </span>
        )}
      </div>
      <p className={local.muted}>
        Units to order each month, already net of what you hold and what is on
        its way. Order by the deadline shown, not in the month itself.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.progress ? 'Sync in progress'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the plan'}
            </strong>
            <div>{error.message}</div>
            <SyncProgress progress={error.progress} />
          </div>
        </div>
      )}

      {loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {data && !data.available && (
        <div className={`${local.banner} ${local.bannerWarn}`}>
          <FiInfo className={local.bannerIcon} />
          <div>
            <strong>Not enough history to plan stock</strong>
            <div>{data.reason}</div>
          </div>
        </div>
      )}

      {/* The assumptions in force, visible and adjustable in place. Shown even
          before a plan exists, so a seller can set them up front. */}
      {inputs && (
        <div className={local.assumptions}>
          <label className={local.field}>
            <span className={assumed.includes('lead_time_days') ? local.assumed : undefined}>
              Lead time, days{assumed.includes('lead_time_days') ? ' (assumed)' : ''}
            </span>
            <input
              type="number" min="0" max="365"
              value={draft?.leadTimeDays ?? ''}
              onChange={(e) => setDraft({ ...draft, leadTimeDays: e.target.value })}
            />
          </label>
          <label className={local.field}>
            <span className={assumed.includes('service_level') ? local.assumed : undefined}>
              Service level{assumed.includes('service_level') ? ' (assumed)' : ''}
            </span>
            <select
              value={draft?.serviceLevel ?? 0.95}
              onChange={(e) => setDraft({ ...draft, serviceLevel: e.target.value })}
            >
              {SERVICE_LEVELS.map((level) => (
                <option key={level} value={level}>{Math.round(level * 100)}%</option>
              ))}
            </select>
          </label>
          <button type="button" className={local.primary}
            onClick={() => setApplied({ ...draft })}>
            Apply
          </button>
          <button type="button" className={local.segment}
            disabled={saving} onClick={persist}>
            {saving ? 'Saving…' : 'Save as mine'}
          </button>
          {plan?.assumptions && (
            <span className={local.muted}>
              Buffer {inr.format(plan.assumptions.safety_units)} units
              {plan.assumptions.sigma_daily !== null
                && ` · demand varies ±${plan.assumptions.sigma_daily} units a day`}
            </span>
          )}
        </div>
      )}

      {plan?.available && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>
                <th>Month</th>
                <th className={local.numeric}>Expected demand</th>
                <th className={local.numeric}>Opening stock</th>
                <th className={local.numeric}>Order</th>
                <th>Order by</th>
              </tr>
            </thead>
            <tbody>
              {months.map((month) => (
                <tr key={month.label}>
                  <td>
                    {month.name} {month.year}
                    {month.events_applied?.length > 0 && (
                      <span className={`${local.pill} ${local.pillEvent}`}>
                        {month.events_applied.map((e) => e.name).join(', ')}
                      </span>
                    )}
                    {/* Not in the order quantity: only comes if the seller takes part. */}
                    {month.sale_events?.length > 0 && month.sale_event_units > 0 && (
                      <span className={local.pill}>
                        +{inr.format(Math.round(month.sale_event_units))} if you join
                        {' '}{month.sale_events.map((e) => e.name).join(', ')}
                      </span>
                    )}
                  </td>
                  <td className={local.numeric}>
                    {inr.format(Math.round(month.demand_units))}
                  </td>
                  <td className={local.numeric}>{inr.format(month.opening_units)}</td>
                  <td className={local.numeric}>
                    {month.order_units > 0
                      ? <span className={local.orderCell}>
                        {inr.format(month.order_units)}
                      </span>
                      : <span className={local.orderNone}>none needed</span>}
                  </td>
                  <td className={month.deadline_passed ? local.peakLate : undefined}>
                    {month.order_units > 0 ? formatDate(month.action_deadline) : '—'}
                    {month.order_units > 0 && month.deadline_passed && ' · passed'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {plan && !plan.available && plan.reason && (
        <div className={local.emptyCell}>{plan.reason}</div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default StockPlan;
